import type { ParameterUnit } from "@mm/api.meteomatics.com";
import { Duration } from "luxon";

export const lightningParamName = "lightnings";

/**
 * Supported lightning visualization modes.
 */
export const lightningRenderModes = ["none", "heatmap", "cluster"] as const;
export type LightningRenderMode = (typeof lightningRenderModes)[number];
export const DefaultLightningRenderMode: LightningRenderMode = "heatmap";

export function isLightningRenderMode(mode: unknown): mode is LightningRenderMode {
  return typeof mode === "string" && (lightningRenderModes as readonly string[]).includes(mode);
}

export const lightningTypeFilterValues = ["cloud_to_cloud", "cloud_to_ground", "unknown"] as const;
export type LightningTypeFilterValue = (typeof lightningTypeFilterValues)[number];

export const lightningColorRange: string[] = [
  "#FFFDAA",
  "#FFE100",
  "#FF7F02",
  "#FF5701",
  "#FF0000",
  "#E00000",
  "#BF0101",
];
export enum LightningDurationType {
  LIGHTNINGS_1 = "lightnings_1",
  LIGHTNINGS_15 = "lightnings_15",
  LIGHTNINGS_30 = "lightnings_30",
  LIGHTNINGS_60 = "lightnings_60",
  LIGHTNINGS_180 = "lightnings_180",
  LIGHTNINGS_360 = "lightnings_360",
  LIGHTNINGS_720 = "lightnings_720",
}
export const DefaultLightningDuration = LightningDurationType.LIGHTNINGS_60;

export const lightningDurations: Record<LightningDurationType, number> = {
  [LightningDurationType.LIGHTNINGS_1]: 1,
  [LightningDurationType.LIGHTNINGS_15]: 15,
  [LightningDurationType.LIGHTNINGS_30]: 30,
  [LightningDurationType.LIGHTNINGS_60]: 60,
  [LightningDurationType.LIGHTNINGS_180]: 180,
  [LightningDurationType.LIGHTNINGS_360]: 360,
  [LightningDurationType.LIGHTNINGS_720]: 720,
};

export function isLightningDuration(param_unit: string): param_unit is LightningDurationType {
  return Object.values(LightningDurationType).includes(param_unit as LightningDurationType);
}
export function getLightningDurationMin(param_unit: string): number {
  if (isLightningDuration(param_unit)) {
    return lightningDurations[param_unit];
  }

  // Fallback to a default duration if the key is invalid
  return lightningDurations[DefaultLightningDuration];
}
export function getPreviousLightningInterval(param_unit: ParameterUnit): ParameterUnit | null {
  const lightningSequence: ParameterUnit[] = [
    LightningDurationType.LIGHTNINGS_1,
    LightningDurationType.LIGHTNINGS_15,
    LightningDurationType.LIGHTNINGS_30,
    LightningDurationType.LIGHTNINGS_60,
    LightningDurationType.LIGHTNINGS_180,
    LightningDurationType.LIGHTNINGS_360,
    LightningDurationType.LIGHTNINGS_720,
  ];

  const currentIndex = lightningSequence.indexOf(param_unit);
  if (currentIndex > 0) {
    return lightningSequence[currentIndex - 1];
  }

  return null; // No previous interval for LIGHTNINGS_1
}

/**
 * Determines if a layer should be visible based on the selected lightning duration.
 * @param selectedDuration - The currently selected lightning duration.
 * @param layerDuration - The duration of the current layer (e.g., LIGHTNINGS_1, LIGHTNINGS_15).
 * @returns true if the layer should be visible, false otherwise.
 */
export function isLayerVisible(selectedDuration: LightningDurationType, layerDuration: LightningDurationType): boolean {
  const selectedDurationMin = lightningDurations[selectedDuration];
  const layerDurationMin = lightningDurations[layerDuration];

  // Layer is visible if its duration is less than or equal to the selected duration.
  return layerDurationMin <= selectedDurationMin;
}

export function getSmallerlightningDurationsAndItself(
  selectedDuration: LightningDurationType,
): LightningDurationType[] {
  const selectedValue = lightningDurations[selectedDuration];
  return Object.entries(lightningDurations)
    .filter(([_, value]) => value <= selectedValue)
    .map(([key]) => key as LightningDurationType);
}

export function getLightningLabel(duration: LightningDurationType): string {
  const minutes = lightningDurations[duration];
  const luxonDuration = Duration.fromObject({ minutes: minutes });
  if (luxonDuration.as("minutes") < 60) {
    return `${luxonDuration.as("minutes")} min`;
  }
  if (luxonDuration.as("minutes") === 60) {
    return luxonDuration.shiftTo("hours").toFormat("h 'hour'");
  }
  return luxonDuration.shiftTo("hours").toFormat("h 'hours'");
}
